import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import * as MUI from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import { tableHeadRowSx } from "../../../../enums/Styles";
import { MASTER_SINGLE_CLINIC } from "../../../../enums/UrlPaths";
import { useGetTiers } from "../../../../hooks/useGetTiers";
import useClinicListParams from "../hooks/useClinicListParams";
import {
  CLINIC_STATUS,
  CLINIC_TYPE_LABELS,
  formatLocation,
  getClinicStatus,
  getClinicType,
} from "../utils/clinicFields";
import { SORTABLE_COLUMNS } from "../utils/sortClinics";

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100];
const NO_WRAP = { whiteSpace: "nowrap" };

const COLUMNS = [
  { id: "select", label: "", width: 48 },
  { id: "rowNumber", label: "#", width: 64 },
  { id: "title", label: "ClinicName", minWidth: 200 },
  { id: "shortName", label: "ShortName" },
  { id: "location", label: "Location", minWidth: 160 },
  { id: "email", label: "Email" },
  { id: "phoneNumber", label: "PhoneNumber" },
  { id: "tier", label: "Tier", width: 140 },
  { id: "clinicType", label: "ClinicType", width: 150 },
  { id: "status", label: "Status" },
  { id: "actions", label: "Actions", align: "right" },
];

const STATUS_CHIPS = {
  [CLINIC_STATUS.NEW]: { label: "NewClinic", color: "info" },
  [CLINIC_STATUS.ACTIVE]: { label: "ClinicActive", color: "success" },
  [CLINIC_STATUS.INACTIVE]: { label: "ClinicInactive", color: "default" },
};

function StatusChip({ clinic }) {
  const { t } = useTranslation();
  const { label, color } = STATUS_CHIPS[getClinicStatus(clinic)];
  return <MUI.Chip size="small" color={color} label={t(label)} />;
}

function HeaderLabel({ column, sort, onSort }) {
  const { t } = useTranslation();
  const label = column.label && t(column.label);
  if (!SORTABLE_COLUMNS.includes(column.id)) return label;
  const isActive = sort.orderBy === column.id;
  return (
    <MUI.TableSortLabel
      active={isActive}
      direction={isActive ? sort.order : "asc"}
      onClick={() => onSort(column.id)}
    >
      {label}
    </MUI.TableSortLabel>
  );
}

function ClinicTypeSelect({ clinic, onTypeChange }) {
  const { t } = useTranslation();
  const clinicType = getClinicType(clinic);
  if (clinic.isPending) return t(CLINIC_TYPE_LABELS[clinicType]);
  return (
    <MUI.Select
      size="small"
      fullWidth
      value={clinicType}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onTypeChange(clinic, event.target.value)}
    >
      {Object.entries(CLINIC_TYPE_LABELS).map(([value, label]) => (
        <MUI.MenuItem key={value} value={value}>
          {t(label)}
        </MUI.MenuItem>
      ))}
    </MUI.Select>
  );
}

function TierSelect({ clinic, onTierChange }) {
  const { t } = useTranslation();
  const { data: tiers } = useGetTiers();
  if (clinic.isPending) return "-";
  return (
    <MUI.Select
      size="small"
      fullWidth
      value={clinic.tierId ?? ""}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) =>
        onTierChange(clinic, tiers.find((tier) => tier.id === event.target.value))
      }
    >
      {(tiers ?? []).map((tier) => (
        <MUI.MenuItem key={tier.id} value={tier.id}>
          {t(tier.title)}
        </MUI.MenuItem>
      ))}
    </MUI.Select>
  );
}

function ClinicTableRow({ clinic, isSelected, actions }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stop = (handler) => (event) => {
    event.stopPropagation();
    handler();
  };

  function handleRowClick() {
    if (clinic.isPending) {
      actions.openDetails(clinic.id);
    } else {
      navigate(`${MASTER_SINGLE_CLINIC}${clinic.title}/${clinic.id}`);
    }
  }

  return (
    <MUI.TableRow
      hover
      selected={isSelected}
      onClick={handleRowClick}
      sx={{
        cursor: "pointer",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      <MUI.TableCell padding="checkbox">
        <MUI.Checkbox
          checked={isSelected}
          onClick={(event) => event.stopPropagation()}
          onChange={() => actions.toggleSelected(clinic.id)}
        />
      </MUI.TableCell>
      <MUI.TableCell>{clinic.rowNumber}</MUI.TableCell>
      <MUI.TableCell sx={{ fontWeight: 600 }}>{clinic.title}</MUI.TableCell>
      <MUI.TableCell sx={NO_WRAP}>{clinic.shortName || "-"}</MUI.TableCell>
      <MUI.TableCell>{formatLocation(clinic) || "-"}</MUI.TableCell>
      <MUI.TableCell>{clinic.email || "-"}</MUI.TableCell>
      <MUI.TableCell sx={NO_WRAP}>{clinic.phoneNumber || "-"}</MUI.TableCell>
      <MUI.TableCell>
        <TierSelect clinic={clinic} onTierChange={actions.changeTier} />
      </MUI.TableCell>
      <MUI.TableCell sx={NO_WRAP}>
        <ClinicTypeSelect clinic={clinic} onTypeChange={actions.changeType} />
      </MUI.TableCell>
      <MUI.TableCell sx={NO_WRAP}>
        <StatusChip clinic={clinic} />
      </MUI.TableCell>
      <MUI.TableCell align="right" sx={NO_WRAP}>
        {clinic.isPending ? (
          <MUI.Tooltip title={t("Edit")}>
            <MUI.IconButton size="small" onClick={stop(() => actions.openDetails(clinic.id))}>
              <EditOutlinedIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Tooltip>
        ) : (
          <>
            <MUI.Tooltip title={t("Details")}>
              <MUI.IconButton size="small" onClick={stop(() => actions.openDetails(clinic.id))}>
                <InfoOutlinedIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
            <MUI.Tooltip title={t(clinic.active ? "Deactivate" : "Activate")}>
              <MUI.IconButton
                size="small"
                color={clinic.active ? "error" : "success"}
                onClick={stop(() => actions.toggleActivation(clinic))}
              >
                <PowerSettingsNewIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
          </>
        )}
      </MUI.TableCell>
    </MUI.TableRow>
  );
}

/**
 * Paginated client-side, returning to the first page whenever search, filters or
 * sort change. Rows arrive already sorted; clicking a header only updates the URL sort.
 * `rowActions`: `{ toggleSelected(id), openDetails(id), changeTier(clinic, tier), changeType(clinic, type), toggleActivation(clinic) }`.
 */
export default function ClinicListTable({ clinics, isLoading, selectedIds, rowActions }) {
  const { t } = useTranslation();
  const { sort, toggleSort, viewKey } = useClinicListParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const lastPage = Math.max(0, Math.ceil(clinics.length / rowsPerPage) - 1);
  const pageRows = clinics.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [viewKey]);

  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table sx={{ minWidth: 1000 }}>
          <MUI.TableHead>
            <MUI.TableRow sx={tableHeadRowSx}>
              {COLUMNS.map((column) => (
                <MUI.TableCell
                  key={column.id}
                  align={column.align}
                  padding={column.id === "select" ? "checkbox" : "normal"}
                  sortDirection={sort.orderBy === column.id ? sort.order : false}
                  sx={{ width: column.width, minWidth: column.minWidth }}
                >
                  <HeaderLabel column={column} sort={sort} onSort={toggleSort} />
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={COLUMNS.length} sx={{ border: 0, py: 6 }}>
                  <MUI.Stack alignItems="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : clinics.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={COLUMNS.length} sx={{ border: 0, py: 3 }}>
                  <SimpleAlert severity="info" message="NoData" />
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              pageRows.map((clinic) => (
                <ClinicTableRow
                  key={clinic.id}
                  clinic={clinic}
                  isSelected={selectedIds.has(clinic.id)}
                  actions={rowActions}
                />
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
      <MUI.TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={clinics.length}
        rowsPerPage={rowsPerPage}
        page={Math.min(page, lastPage)}
        labelRowsPerPage={t("RowsPerPage")}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        backIconButtonProps={{ title: t("PreviousPage") }}
        nextIconButtonProps={{ title: t("NextPage") }}
      />
    </MUI.Paper>
  );
}
